import { Injectable } from '@angular/core';
import { Observable, catchError, iif, map, of, tap } from 'rxjs';
import { CreateZoneDeviceDTO, ExecutionLocation, FilterDTO, FilterOperator, Role, SortDirection, UpdateZoneDeviceDTO, UserAccountClient, ZoneDeviceBrand, ZoneDeviceClient, ZoneDeviceDTO, ZoneDeviceSpecificationDTO, ZoneDeviceType } from 'src/app/base/models/models.swagger.generated';
import { ToastService } from 'src/app/base/services/toast.service';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';

export interface IProxyUserInfo {
  id: string;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class ZoneDeviceDetailsService {

  public isBusy = false;
  private allSpecs: ZoneDeviceSpecificationDTO[];
  private proxyUsers: IProxyUserInfo[];
  private proxyUsersClientId: number;

  constructor(
    private zoneDeviceClient: ZoneDeviceClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService,
    private userAccountClient: UserAccountClient
  ) { }

  public reset(): void {
    this.isBusy = false;
  }

  public initializeServiceData(): Observable<ZoneDeviceSpecificationDTO[]> {
    // reset proxy users list
    this.proxyUsers = null;
    return this.loadAvailableDeviceSpecifications();
  }

  public loadAvailableDeviceSpecifications(): Observable<ZoneDeviceSpecificationDTO[]> {
    return iif(() => this.allSpecs != null, of(this.allSpecs), this.zoneDeviceClient.getAvailableDeviceSpecifications())
      .pipe(
        tap(specs => {
          this.allSpecs = specs;
        })
      );
  }

  public getZoneDeviceInitialStructure(deviceType: ZoneDeviceType, brand: ZoneDeviceBrand): Observable<ZoneDeviceDTO> {
    return this.zoneDeviceClient.getZoneDeviceInitialStructure(deviceType, brand);
  }

  public getZoneDevice(id: number): Observable<ZoneDeviceDTO> {
    this.spinnerService.showSpinner = true;
    this.isBusy = true;
    return this.zoneDeviceClient.getDevice(id).pipe(
      tap(() => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
      }),
      catchError((error: any) => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        return of(null);
      }
    ));
  }

  public createZoneDevice(device: CreateZoneDeviceDTO): Observable<ZoneDeviceDTO> {
    this.isBusy = true;
    this.spinnerService.showSpinner = true;
    return this.zoneDeviceClient.createDevice(device).pipe(
      tap(() => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess('i18n.domain.administration.zone-device-details.messages.add-title', 'i18n.domain.administration.zone-device-details.messages.add-success-message');
      }),
      catchError((error: any) => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        return of(null);
      }
    ));
  }

  public updateZoneDevice(device: UpdateZoneDeviceDTO): Observable<ZoneDeviceDTO> {
    this.isBusy = true;
    this.spinnerService.showSpinner = true;
    return this.zoneDeviceClient.updateDevice(device).pipe(
      tap(() => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess('i18n.domain.administration.zone-device-details.messages.update-title', 'i18n.domain.administration.zone-device-details.messages.update-success-message');
      }),
      catchError((error: any) => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        return of(null);
      }
    ));
  }

  public deleteZoneDevice(id: number): Observable<any> {
    this.isBusy = true;
    this.spinnerService.showSpinner = true;
    return this.zoneDeviceClient.deleteDevice(id).pipe(
      tap(() => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess('i18n.domain.administration.zone-device-details.messages.delete-title', 'i18n.domain.administration.zone-device-details.messages.delete-success-message');
      }),
      catchError((error: any) => {
        this.isBusy = false;
        this.spinnerService.showSpinner = false;
        this.toastService.showError('i18n.errors.server-error.title', 'i18n.errors.server-error.message');
        return of(null);
      }
    ));
  }

  public isDeviceProxyDriven(deviceType: ZoneDeviceType,brand: ZoneDeviceBrand): boolean {
    const spec = this.allSpecs.find(spec => spec.deviceType === deviceType && spec.brand === brand);
    return spec.execLocation === ExecutionLocation.RunOnProxy;
  }

  public getProxyUsers(clientId: number): Observable<IProxyUserInfo[]> {
    return iif(() => this.proxyUsersClientId == clientId && this.proxyUsers != null, of(this.proxyUsers), this.loadProxyUsers(clientId));
  }

  private loadProxyUsers(clientId: number): Observable<IProxyUserInfo[]> {
    const filters: FilterDTO[] = [
      new FilterDTO({isComplex: false, field: 'Enabled', operator: FilterOperator.Equal, value: 'true'}),
      new FilterDTO({isComplex: false, field: 'Roles', operator: FilterOperator.Equal, value: Role.Proxy.toString()})
    ];
    return this.userAccountClient.searchUsers(filters, clientId, 0, 0, 'UserName', SortDirection.Ascending).pipe(
      map(result => {
        this.proxyUsersClientId = clientId;
        this.proxyUsers = result.items.map((item) => {
          return {id: item.userId, name: item.userName};
        });
        return this.proxyUsers;
      })
    );
  }
}
